import { useEffect } from 'react';
import { Outlet, useLocation, NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ArrowRightOnRectangleIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { MobilePanelProvider, useRightPanelContent } from './MobilePanelContext';
import { AIChatButton, AIChatModal } from '@/components/ai';
import SkyToggle from '@/components/ui/sky-toggle';
import { cn } from '@/utils/helpers';
import { useAppSelector, useAppDispatch } from '@/hooks';
import { setMobileLeftDrawerOpen, setMobileRightDrawerOpen, toggleTheme } from '@/store/slices/uiSlice';
import { logout } from '@/store/slices/authSlice';

// Background configurations with visible patterns per route
const routeBackgrounds: Record<string, {
  gradient: string;
  pattern: 'grid' | 'dots' | 'lines' | 'waves' | 'hexagon';
  accentColor: string;
  sidebarAccent: string;
  patternColor: string;
  patternColorDark: string;
}> = {
  '/': {
    gradient: 'from-orange-50/50 via-amber-50/30 to-slate-50 dark:from-slate-950 dark:via-orange-950/20 dark:to-slate-900',
    pattern: 'dots',
    accentColor: 'orange',
    sidebarAccent: 'from-orange-500/20 via-amber-500/10 to-transparent',
    patternColor: '#f97316',
    patternColorDark: '#fb923c',
  },
  '/planner': {
    gradient: 'from-amber-50 via-orange-50/50 to-slate-50 dark:from-slate-950 dark:via-amber-950/30 dark:to-slate-900',
    pattern: 'grid',
    accentColor: 'amber',
    sidebarAccent: 'from-amber-500/20 via-orange-500/10 to-transparent',
    patternColor: '#f59e0b',
    patternColorDark: '#fbbf24',
  },
  '/streaks': {
    gradient: 'from-rose-50 via-red-50/50 to-slate-50 dark:from-slate-950 dark:via-rose-950/30 dark:to-slate-900',
    pattern: 'waves',
    accentColor: 'rose',
    sidebarAccent: 'from-rose-500/20 via-red-500/10 to-transparent',
    patternColor: '#f43f5e',
    patternColorDark: '#fb7185',
  },
  '/tasks': {
    gradient: 'from-emerald-50 via-teal-50/50 to-slate-50 dark:from-slate-950 dark:via-emerald-950/30 dark:to-slate-900',
    pattern: 'dots',
    accentColor: 'emerald',
    sidebarAccent: 'from-emerald-500/20 via-teal-500/10 to-transparent',
    patternColor: '#10b981',
    patternColorDark: '#34d399',
  },
  '/tracks': {
    gradient: 'from-indigo-50 via-blue-50/50 to-slate-50 dark:from-slate-950 dark:via-indigo-950/30 dark:to-slate-900',
    pattern: 'lines',
    accentColor: 'indigo',
    sidebarAccent: 'from-indigo-500/20 via-blue-500/10 to-transparent',
    patternColor: '#6366f1',
    patternColorDark: '#818cf8',
  },
  '/timeline': {
    gradient: 'from-cyan-50 via-teal-50/50 to-slate-50 dark:from-slate-950 dark:via-cyan-950/30 dark:to-slate-900',
    pattern: 'lines',
    accentColor: 'cyan',
    sidebarAccent: 'from-cyan-500/20 via-teal-500/10 to-transparent',
    patternColor: '#06b6d4',
    patternColorDark: '#22d3ee',
  },
  '/analytics': {
    gradient: 'from-fuchsia-50 via-pink-50/50 to-slate-50 dark:from-slate-950 dark:via-fuchsia-950/30 dark:to-slate-900',
    pattern: 'grid',
    accentColor: 'fuchsia',
    sidebarAccent: 'from-fuchsia-500/20 via-pink-500/10 to-transparent',
    patternColor: '#d946ef',
    patternColorDark: '#e879f9',
  },
  '/notes': {
    gradient: 'from-yellow-50 via-amber-50/50 to-slate-50 dark:from-slate-950 dark:via-yellow-950/30 dark:to-slate-900',
    pattern: 'dots',
    accentColor: 'yellow',
    sidebarAccent: 'from-yellow-500/20 via-amber-500/10 to-transparent',
    patternColor: '#eab308',
    patternColorDark: '#facc15',
  },
  '/blog': {
    gradient: 'from-yellow-50 via-amber-50/50 to-slate-50 dark:from-slate-950 dark:via-yellow-950/30 dark:to-slate-900',
    pattern: 'dots',
    accentColor: 'yellow',
    sidebarAccent: 'from-yellow-500/20 via-amber-500/10 to-transparent',
    patternColor: '#eab308',
    patternColorDark: '#facc15',
  },
  '/settings': {
    gradient: 'from-slate-100 via-gray-50 to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900',
    pattern: 'dots',
    accentColor: 'slate',
    sidebarAccent: 'from-slate-500/20 via-gray-500/10 to-transparent',
    patternColor: '#64748b',
    patternColorDark: '#94a3b8',
  },
};

const defaultBackground = routeBackgrounds['/'];

// Clean white pages use PageLayout and handle their own styling
const cleanWhitePages = ['/blog', '/notes', '/tracks', '/planner', '/settings', '/habits'];
const isCleanWhitePage = (pathname: string) =>
  cleanWhitePages.some(page => pathname.startsWith(page));

// Pattern Components with better visibility
function GridPattern({ color, colorDark }: { color: string; colorDark: string }) {
  return (
    <>
      <svg className="absolute inset-0 w-full h-full dark:hidden" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="grid-light" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke={color} strokeWidth="1" opacity="0.15" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid-light)" />
      </svg>
      <svg className="absolute inset-0 w-full h-full hidden dark:block" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="grid-dark" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke={colorDark} strokeWidth="1" opacity="0.2" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid-dark)" />
      </svg>
    </>
  );
}

function DotsPattern({ color, colorDark }: { color: string; colorDark: string }) {
  return (
    <>
      <svg className="absolute inset-0 w-full h-full dark:hidden" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="dots-light" width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="10" cy="10" r="1.5" fill={color} opacity="0.2" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#dots-light)" />
      </svg>
      <svg className="absolute inset-0 w-full h-full hidden dark:block" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="dots-dark" width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="10" cy="10" r="1.5" fill={colorDark} opacity="0.25" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#dots-dark)" />
      </svg>
    </>
  );
}

function LinesPattern({ color, colorDark }: { color: string; colorDark: string }) {
  return (
    <>
      <svg className="absolute inset-0 w-full h-full dark:hidden" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="lines-light" width="50" height="50" patternUnits="userSpaceOnUse">
            <line x1="0" y1="0" x2="50" y2="50" stroke={color} strokeWidth="1" opacity="0.12" />
            <line x1="50" y1="0" x2="0" y2="50" stroke={color} strokeWidth="1" opacity="0.12" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#lines-light)" />
      </svg>
      <svg className="absolute inset-0 w-full h-full hidden dark:block" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="lines-dark" width="50" height="50" patternUnits="userSpaceOnUse">
            <line x1="0" y1="0" x2="50" y2="50" stroke={colorDark} strokeWidth="1" opacity="0.18" />
            <line x1="50" y1="0" x2="0" y2="50" stroke={colorDark} strokeWidth="1" opacity="0.18" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#lines-dark)" />
      </svg>
    </>
  );
}

function WavesPattern({ color, colorDark }: { color: string; colorDark: string }) {
  return (
    <>
      <svg className="absolute inset-0 w-full h-full dark:hidden" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
        <defs>
          <pattern id="waves-light" width="120" height="60" patternUnits="userSpaceOnUse">
            <path d="M0 30 Q30 0 60 30 T120 30" fill="none" stroke={color} strokeWidth="1.5" opacity="0.15" />
            <path d="M0 45 Q30 15 60 45 T120 45" fill="none" stroke={color} strokeWidth="1" opacity="0.1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#waves-light)" />
      </svg>
      <svg className="absolute inset-0 w-full h-full hidden dark:block" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
        <defs>
          <pattern id="waves-dark" width="120" height="60" patternUnits="userSpaceOnUse">
            <path d="M0 30 Q30 0 60 30 T120 30" fill="none" stroke={colorDark} strokeWidth="1.5" opacity="0.2" />
            <path d="M0 45 Q30 15 60 45 T120 45" fill="none" stroke={colorDark} strokeWidth="1" opacity="0.15" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#waves-dark)" />
      </svg>
    </>
  );
}


function HexagonPattern({ color, colorDark }: { color: string; colorDark: string }) {
  return (
    <>
      <svg className="absolute inset-0 w-full h-full dark:hidden" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="hex-light" width="56" height="100" patternUnits="userSpaceOnUse">
            <path d="M28 66L0 50L0 16L28 0L56 16L56 50L28 66L28 100" fill="none" stroke={color} strokeWidth="1" opacity="0.12" />
            <path d="M28 0L28 34L0 50L0 84L28 100L56 84L56 50L28 34" fill="none" stroke={color} strokeWidth="0.5" opacity="0.08" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#hex-light)" />
      </svg>
      <svg className="absolute inset-0 w-full h-full hidden dark:block" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="hex-dark" width="56" height="100" patternUnits="userSpaceOnUse">
            <path d="M28 66L0 50L0 16L28 0L56 16L56 50L28 66L28 100" fill="none" stroke={colorDark} strokeWidth="1" opacity="0.18" />
            <path d="M28 0L28 34L0 50L0 84L28 100L56 84L56 50L28 34" fill="none" stroke={colorDark} strokeWidth="0.5" opacity="0.12" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#hex-dark)" />
      </svg>
    </>
  );
}

function BackgroundPattern({ pattern, color, colorDark }: { pattern: string; color: string; colorDark: string }) {
  switch (pattern) {
    case 'grid':
      return <GridPattern color={color} colorDark={colorDark} />;
    case 'dots':
      return <DotsPattern color={color} colorDark={colorDark} />;
    case 'lines':
      return <LinesPattern color={color} colorDark={colorDark} />;
    case 'waves':
      return <WavesPattern color={color} colorDark={colorDark} />;
    case 'hexagon':
      return <HexagonPattern color={color} colorDark={colorDark} />;
    default:
      return <DotsPattern color={color} colorDark={colorDark} />;
  }
}

// ─── Navigation Icons (same as Sidebar.tsx, simplified for mobile drawer) ───

const PlannerIcon = ({ size = 32 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dCalBg" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#F9FAFB"/><stop offset="100%" stopColor="#F3F4F6"/></linearGradient>
      <linearGradient id="dCalRed" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#EF4444"/><stop offset="100%" stopColor="#DC2626"/></linearGradient>
    </defs>
    <rect x="10" y="10" width="100" height="100" rx="22" fill="url(#dCalBg)" stroke="#D1D5DB" strokeWidth="1.5"/>
    <path d="M10.5 32 Q10.5 10.5 32 10.5 L88 10.5 Q109.5 10.5 109.5 32 L109.5 38 L10.5 38 Z" fill="url(#dCalRed)"/>
    <circle cx="35" cy="24" r="4" fill="#FCA5A5"/><circle cx="85" cy="24" r="4" fill="#FCA5A5"/>
    <text x="60" y="82" textAnchor="middle" fontSize="42" fontWeight="bold" fill="#1F2937">17</text>
  </svg>
);

const TracksIcon = ({ size = 32 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs><linearGradient id="dTracksBg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#A78BFA"/><stop offset="100%" stopColor="#7C3AED"/></linearGradient></defs>
    <rect x="10" y="10" width="100" height="100" rx="22" fill="url(#dTracksBg)"/>
    <path d="M32 90 L32 70 L55 70 L55 50 L78 50 L78 30 L92 30" stroke="white" strokeOpacity="0.35" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <circle cx="32" cy="90" r="10" fill="#10B981"/><path d="M27 90 L30 93 L37 86" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="55" cy="70" r="10" fill="#10B981"/><path d="M50 70 L53 73 L60 66" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="78" cy="50" r="10" fill="#10B981"/><path d="M73 50 L76 53 L83 46" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="92" cy="30" r="12" fill="white"/><circle cx="92" cy="30" r="6" fill="#7C3AED"/>
  </svg>
);

const HabitsIcon = ({ size = 32 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dHabitH" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#FDBA74"/><stop offset="100%" stopColor="#FB923C"/></linearGradient>
      <linearGradient id="dHabitP" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#F9FAFB"/><stop offset="100%" stopColor="#F3F4F6"/></linearGradient>
    </defs>
    <rect x="10" y="10" width="100" height="100" rx="22" fill="url(#dHabitP)" stroke="#D1D5DB" strokeWidth="1.5"/>
    <path d="M10.5 32 Q10.5 10.5 32 10.5 L88 10.5 Q109.5 10.5 109.5 32 L109.5 36 L10.5 36 Z" fill="url(#dHabitH)"/>
    <rect x="26" y="44" width="14" height="14" rx="3" fill="#F97316"/><path d="M29 51 L33 55 L40 48" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/><rect x="48" y="47" width="40" height="8" rx="4" fill="#FDBA74"/>
    <rect x="26" y="64" width="14" height="14" rx="3" fill="#F97316"/><path d="M29 71 L33 75 L40 68" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/><rect x="48" y="67" width="32" height="8" rx="4" fill="#FDBA74"/>
    <rect x="26" y="84" width="14" height="14" rx="3" fill="white" stroke="#FDBA74" strokeWidth="1.5"/><rect x="48" y="87" width="36" height="8" rx="4" fill="#FED7AA"/>
  </svg>
);

const NotesIcon = ({ size = 32 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dNoteY" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#FCD34D"/><stop offset="100%" stopColor="#FBBF24"/></linearGradient>
      <linearGradient id="dNoteP" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#F9FAFB"/><stop offset="100%" stopColor="#F3F4F6"/></linearGradient>
    </defs>
    <rect x="10" y="10" width="100" height="100" rx="22" fill="url(#dNoteP)" stroke="#D1D5DB" strokeWidth="1.5"/>
    <path d="M10.5 32 L10.5 32 Q10.5 10.5 32 10.5 L88 10.5 Q109.5 10.5 109.5 32 L109.5 35 L10.5 35 Z" fill="url(#dNoteY)"/>
    <line x1="25" y1="50" x2="95" y2="50" stroke="#D1D5DB" strokeWidth="2"/><line x1="25" y1="65" x2="95" y2="65" stroke="#D1D5DB" strokeWidth="2"/>
    <line x1="25" y1="80" x2="95" y2="80" stroke="#D1D5DB" strokeWidth="2"/><line x1="25" y1="95" x2="95" y2="95" stroke="#D1D5DB" strokeWidth="2"/>
  </svg>
);

const BlogIcon = ({ size = 32 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs><linearGradient id="dBlogBg" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#F97316"/><stop offset="100%" stopColor="#EA580C"/></linearGradient></defs>
    <rect x="10" y="10" width="100" height="100" rx="22" fill="url(#dBlogBg)"/>
    <g transform="translate(60,60) rotate(45)"><rect x="-8" y="-35" width="16" height="55" rx="2" fill="white"/><path d="M-8,20 L0,38 L8,20" fill="white"/><rect x="-8" y="-35" width="16" height="10" rx="2" fill="#FDBA74"/></g>
  </svg>
);

const SettingsIcon = ({ size = 32 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs><linearGradient id="dSetBg" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#94A3B8"/><stop offset="100%" stopColor="#64748B"/></linearGradient></defs>
    <rect x="10" y="10" width="100" height="100" rx="22" fill="url(#dSetBg)"/>
    <rect x="28" y="35" width="64" height="6" rx="3" fill="white" fillOpacity="0.4"/><circle cx="72" cy="38" r="10" fill="white"/>
    <rect x="28" y="57" width="64" height="6" rx="3" fill="white" fillOpacity="0.4"/><circle cx="48" cy="60" r="10" fill="white"/>
    <rect x="28" y="79" width="64" height="6" rx="3" fill="white" fillOpacity="0.4"/><circle cx="62" cy="82" r="10" fill="white"/>
  </svg>
);

const drawerNavItems = [
  { name: 'Planner', href: '/planner', IconComponent: PlannerIcon },
  { name: 'Tracks', href: '/tracks', IconComponent: TracksIcon },
  { name: 'Habits', href: '/habits', IconComponent: HabitsIcon },
  { name: 'Notes', href: '/notes', IconComponent: NotesIcon },
  { name: 'Blog', href: '/blog', IconComponent: BlogIcon },
];

// ─── Default Right Panel (pages without page-specific sidebar) ───

function DefaultRightPanel() {
  const { user } = useAppSelector((state) => state.auth);
  const { theme } = useAppSelector((state) => state.ui);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      {/* User info */}
      <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center text-white font-semibold">
          {user?.name?.charAt(0).toUpperCase() || 'U'}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user?.name}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
        </div>
      </div>

      {/* Theme toggle */}
      <div className="flex items-center justify-between px-1">
        <span className="text-sm text-gray-600 dark:text-gray-400">Dark mode</span>
        <SkyToggle
          checked={theme === 'dark'}
          onChange={() => dispatch(toggleTheme())}
          size={10}
        />
      </div>

      {/* Notifications */}
      <div>
        <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3 px-1">Notifications</h3>
        <div className="text-sm text-gray-400 dark:text-gray-500 py-4 text-center">
          No new notifications
        </div>
      </div>

      {/* Quick links */}
      <div>
        <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 px-1">Quick Links</h3>
        <button
          onClick={() => { navigate('/settings'); dispatch(setMobileRightDrawerOpen(false)); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <Cog6ToothIcon className="w-5 h-5 text-gray-400" />
          <span className="text-sm text-gray-700 dark:text-gray-300">Settings</span>
        </button>
        <button
          onClick={() => { dispatch(logout()); navigate('/'); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          <ArrowRightOnRectangleIcon className="w-5 h-5 text-red-400" />
          <span className="text-sm text-red-600 dark:text-red-400">Sign out</span>
        </button>
      </div>
    </div>
  );
}

// ─── Mobile Drawers ───

function MobileDrawers() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { mobileLeftDrawerOpen, mobileRightDrawerOpen, theme } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  const rightPanelContent = useRightPanelContent();

  // Close drawers on route change
  useEffect(() => {
    dispatch(setMobileLeftDrawerOpen(false));
    dispatch(setMobileRightDrawerOpen(false));
  }, [location.pathname, dispatch]);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = (mobileLeftDrawerOpen || mobileRightDrawerOpen) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileLeftDrawerOpen, mobileRightDrawerOpen]);

  return (
    <div className="lg:hidden">
      {/* ── Left Drawer (Navigation) ── */}
      <AnimatePresence>
        {mobileLeftDrawerOpen && (
          <>
            <motion.div
              key="left-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 bg-black/40"
              onClick={() => dispatch(setMobileLeftDrawerOpen(false))}
            />
            <motion.aside
              key="left-drawer"
              initial={{ x: '-105%' }}
              animate={{ x: 0 }}
              exit={{ x: '-105%' }}
              transition={{ type: 'tween', duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
              className="fixed -left-1 top-0 bottom-0 z-50 w-[calc(18rem+0.25rem)] pr-0 pl-1 bg-white dark:bg-black border-r border-gray-200 dark:border-gray-800 overflow-y-auto flex flex-col"
            >
              <div className="flex flex-col h-full px-4 py-5">
                {/* Logo + Close */}
                <div className="flex items-center justify-between mb-8 px-2">
                  <div className="flex items-center gap-3">
                    <svg width="28" height="28" viewBox="30 50 300 330" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <linearGradient id="dTop" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#FFEEE4"/><stop offset="100%" stopColor="#FFE4D6"/></linearGradient>
                        <linearGradient id="dLeft" x1="100%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#FDBA74"/><stop offset="100%" stopColor="#FB923C"/></linearGradient>
                        <linearGradient id="dRight" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#F97316"/><stop offset="100%" stopColor="#EA580C"/></linearGradient>
                      </defs>
                      <g><path d="M40 250 L40 340 L100 370 L100 280 Z" fill="url(#dLeft)"/><path d="M100 280 L100 370 L160 340 L160 250 Z" fill="url(#dRight)"/><path d="M40 250 L100 220 L160 250 L100 280 Z" fill="url(#dTop)"/></g>
                      <g><path d="M120 180 L120 340 L180 370 L180 210 Z" fill="url(#dLeft)"/><path d="M180 210 L180 370 L240 340 L240 180 Z" fill="url(#dRight)"/><path d="M120 180 L180 150 L240 180 L180 210 Z" fill="url(#dTop)"/></g>
                      <g><path d="M200 90 L200 340 L260 370 L260 120 Z" fill="url(#dLeft)"/><path d="M260 120 L260 370 L320 340 L320 90 Z" fill="url(#dRight)"/><path d="M200 90 L260 60 L320 90 L260 120 Z" fill="url(#dTop)"/></g>
                    </svg>
                    <span className="text-xl font-semibold text-gray-900 dark:text-white">DayDrive</span>
                  </div>
                  <button
                    onClick={() => dispatch(setMobileLeftDrawerOpen(false))}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                {/* Navigation */}
                <nav className="flex-1">
                  <ul className="space-y-1">
                    {drawerNavItems.map((item) => {
                      const isActive = location.pathname.startsWith(item.href);
                      return (
                        <li key={item.name}>
                          <NavLink
                            to={item.href}
                            onClick={() => dispatch(setMobileLeftDrawerOpen(false))}
                            className={cn(
                              'flex items-center gap-3 px-2 py-2 rounded-xl transition-all duration-150',
                              isActive
                                ? 'bg-gray-100/80 dark:bg-gray-800/80'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-900/50'
                            )}
                          >
                            <item.IconComponent size={32} />
                            <span className={cn(
                              'text-[15px]',
                              isActive ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-600 dark:text-gray-300'
                            )}>
                              {item.name}
                            </span>
                          </NavLink>
                        </li>
                      );
                    })}
                    {/* Settings */}
                    <li>
                      <NavLink
                        to="/settings"
                        onClick={() => dispatch(setMobileLeftDrawerOpen(false))}
                        className={cn(
                          'flex items-center gap-3 px-2 py-2 rounded-xl transition-all duration-150',
                          location.pathname.startsWith('/settings')
                            ? 'bg-gray-100/80 dark:bg-gray-800/80'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-900/50'
                        )}
                      >
                        <SettingsIcon size={32} />
                        <span className={cn(
                          'text-[15px]',
                          location.pathname.startsWith('/settings') ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-600 dark:text-gray-300'
                        )}>
                          Settings
                        </span>
                      </NavLink>
                    </li>
                  </ul>
                </nav>

                {/* Bottom Section */}
                <div className="mt-auto pt-4 border-t border-gray-100 dark:border-gray-800 space-y-1">
                  {/* Theme Toggle */}
                  <div className="flex items-center py-2 px-3">
                    <SkyToggle
                      checked={theme === 'dark'}
                      onChange={() => dispatch(toggleTheme())}
                      size={10}
                    />
                  </div>

                  {/* Logout */}
                  <button
                    onClick={() => { dispatch(logout()); navigate('/'); dispatch(setMobileLeftDrawerOpen(false)); }}
                    className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-all duration-200"
                  >
                    <div className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
                      <ArrowRightOnRectangleIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </div>
                    <span className="text-[15px] text-gray-600 dark:text-gray-300">Log out</span>
                  </button>

                  {/* User Profile */}
                  <NavLink
                    to="/settings"
                    onClick={() => dispatch(setMobileLeftDrawerOpen(false))}
                    className="flex items-center gap-2.5 px-2 py-1.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-all duration-200"
                  >
                    <div className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-pink-500">
                      <span className="text-sm font-medium text-white">
                        {user?.name?.substring(0, 1).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <span className="text-[15px] text-gray-600 dark:text-gray-300 truncate">
                      {user?.name || 'Profile'}
                    </span>
                  </NavLink>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ── Right Drawer (Page sidebar / Default panel) ── */}
      <AnimatePresence>
        {mobileRightDrawerOpen && (
          <>
            <motion.div
              key="right-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
              onClick={() => dispatch(setMobileRightDrawerOpen(false))}
            />
            <motion.aside
              key="right-drawer"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed right-0 top-0 bottom-0 z-50 w-80 bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 overflow-y-auto flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800 shrink-0">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {rightPanelContent ? 'Widgets' : 'Menu'}
                </span>
                <button
                  onClick={() => dispatch(setMobileRightDrawerOpen(false))}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {rightPanelContent || <DefaultRightPanel />}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Main Layout Content ───

function MainLayoutContent() {
  const { sidebarCollapsed } = useAppSelector((state) => state.ui);
  const location = useLocation();

  const getBackgroundConfig = () => {
    const path = location.pathname;
    if (routeBackgrounds[path]) return routeBackgrounds[path];
    const basePath = '/' + path.split('/')[1];
    return routeBackgrounds[basePath] || defaultBackground;
  };

  const bgConfig = getBackgroundConfig();

  return (
    <div className="min-h-screen relative">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <Sidebar bgConfig={bgConfig} />
      </div>

      {/* Mobile Top Bar */}
      <div className="lg:hidden">
        <Header />
      </div>

      {/* Mobile Drawers */}
      <MobileDrawers />

      {/* Main Content Area */}
      <main
        className={cn(
          'min-h-screen transition-all duration-300',
          sidebarCollapsed ? 'lg:pl-[72px]' : 'lg:pl-[244px]'
        )}
      >
        {isCleanWhitePage(location.pathname) ? (
          <Outlet />
        ) : (
          <div className="relative min-h-screen">
            <div className="absolute inset-0 overflow-hidden">
              <div className={cn(
                'absolute inset-0 bg-gradient-to-br transition-all duration-500',
                bgConfig.gradient
              )} />
              {/* SVG patterns + blur blobs only on desktop — saves GPU/battery on mobile */}
              <div className="hidden lg:block">
                <BackgroundPattern
                  pattern={bgConfig.pattern}
                  color={bgConfig.patternColor}
                  colorDark={bgConfig.patternColorDark}
                />
                <div
                  className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full blur-3xl opacity-20 dark:opacity-15 transition-all duration-700"
                  style={{ background: `radial-gradient(circle, ${bgConfig.patternColor}40, transparent)` }}
                />
                <div
                  className="absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full blur-3xl opacity-15 dark:opacity-10 transition-all duration-700"
                  style={{ background: `radial-gradient(circle, ${bgConfig.patternColor}30, transparent)` }}
                />
              </div>
            </div>
            <div className="relative z-10 min-h-screen pt-12 lg:pt-8 px-4 pb-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
              <Outlet />
            </div>
          </div>
        )}
      </main>

      {/* AI Assistant — hidden on mobile for pages with their own bottom tab bar */}
      <AIChatButton className={location.pathname === '/planner' || location.pathname.startsWith('/tracks') || location.pathname === '/habits' || location.pathname === '/notes' || location.pathname.startsWith('/blog') || location.pathname === '/settings' ? 'hidden lg:block' : ''} />
      <AIChatModal />
    </div>
  );
}

// ─── Exported MainLayout (wraps content in MobilePanelProvider) ───

export function MainLayout() {
  return (
    <MobilePanelProvider>
      <MainLayoutContent />
    </MobilePanelProvider>
  );
}

export { routeBackgrounds };
